// Post-checkout landing page. Polar redirects here after a successful
// checkout; the webhook fires concurrently and grants credits. We poll
// /api/billing/balance for up to ~15s waiting for the balance to
// change, then show a confirmation. If the balance doesn't change in
// time, we still show success — the webhook may just be slow and the
// user doesn't need to be held here.

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Coins, ArrowRight } from "lucide-react";
import { getBalance, type CreditBalance } from "../lib/api";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [beforeTotal, setBeforeTotal] = useState<number | null>(null);
  const [pollingDone, setPollingDone] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const initial = await getBalance();
        if (cancelled) return;
        setBeforeTotal(initial.total);
        setBalance(initial);
      } catch {
        // Balance unreachable — still show success, just can't show delta.
        setPollingDone(true);
        return;
      }

      while (!cancelled && Date.now() - startRef.current < 15_000) {
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) return;
        try {
          const next = await getBalance();
          setBalance(next);
          if (beforeTotal !== null && next.total > beforeTotal) break;
          if (next.plan_id !== "free") break;
        } catch {
          // ignore transient failures
        }
      }
      if (!cancelled) setPollingDone(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gained =
    beforeTotal !== null && balance ? balance.total - beforeTotal : 0;

  return (
    <div className="mx-auto max-w-xl px-6 md:px-10 py-16 text-center space-y-6">
      <div className="mx-auto h-16 w-16 rounded-full bg-success/15 grid place-items-center">
        <CheckCircle2 size={40} strokeWidth={1.5} className="text-success" />
      </div>
      <div>
        <div className="eyebrow">Receipt</div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-medium tracking-[-0.02em]">
          Payment received.
        </h1>
        <p className="text-sm text-fg-muted mt-3">
          {pollingDone && gained === 0
            ? "Your credits should appear momentarily. Refresh if the sidebar hasn't updated."
            : "Crediting your account…"}
        </p>
      </div>

      {balance && (
        <div className="surface p-5 space-y-3 text-left">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-fg-subtle">
              <Coins size={14} strokeWidth={1.75} /> Balance
            </div>
            {gained > 0 && (
              <div className="text-xs text-success font-mono tabular-nums">
                +{gained}
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-mono tabular-nums text-3xl">
              {balance.total.toLocaleString()}
            </span>
            <span className="text-sm text-fg-subtle">credits</span>
          </div>
          <div className="text-xs text-fg-subtle">
            Plan: <span className="capitalize text-fg-muted">{balance.plan_id}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 pt-2">
        <Link to="/" className="btn-primary text-sm">
          Build a deck <ArrowRight size={14} strokeWidth={2} />
        </Link>
        <Link to="/billing" className="btn-secondary text-sm">
          Back to billing
        </Link>
      </div>

      {params.get("checkout_id") && (
        <div className="text-[10px] text-fg-subtle font-mono">
          Checkout #{params.get("checkout_id")}
        </div>
      )}
    </div>
  );
}
